import { html, TemplateResult } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { DodonaElement } from "@/components/meta/dodona_element";
import { I18n } from "@/i18n/i18n";
import "@/components/feedback/feedback_context";
import { renderMessages } from "@/feedback/message_helpers";
import type { Context, DiffStyle, FeedbackEnvelopeContext, Tab, Test } from "@/types/FeedbackResult";
import { pythiaDiffStyle } from "@/feedback/pythia";
import { initTooltips } from "@/utilities";
import { sessionStore } from "@/storage";
import { countGroups } from "@/feedback/test_counts";

export function tabId(i: number): string {
    return `tab-${i}`;
}

/**
 * CSV always uses split (columns align better side-by-side).
 * Pythia tests delegate to pythiaDiffStyle (same 55-char threshold).
 * For plain text: unified when any line exceeds 55 chars (long lines are
 * easier to read stacked), split otherwise.
 */
function testDiffStyle(test: Test): DiffStyle {
    if (test.data?.diff) return pythiaDiffStyle(test.data.diff);
    if (test.format === "csv") return "split";
    const combined = `${test.expected ?? ""}\n${test.generated ?? ""}`;
    for (const l of combined.split("\n")) {
        if (l.length >= 55) {
            return "unified";
        }
    }
    return "split";
}

// A single test needing unified is enough to switch the whole tab.
export function tabDiffStyle(tab: Tab): DiffStyle {
    for (const group of tab.groups ?? []) {
        for (const testcase of group.groups ?? []) {
            for (const test of testcase.tests ?? []) {
                if (testDiffStyle(test) === "unified") return "unified";
            }
        }
    }
    return "split";
}

/**
 * Renders one tab panel (top-level group) of the feedback table.
 * Manages its own diff-style toggle (split / unified), defaulting to the
 * style that best fits the tab's content, and owns the collapse state of its
 * contexts: both the per-group chevrons and the show/hide-correct switch write
 * to it, and it is persisted in sessionStorage so it survives page reloads.
 *
 * @element d-feedback-tab
 *
 * @prop {Tab}                     tab               - Tab data (groups, messages, …).
 * @prop {number}                  tab-index         - Zero-based position of this tab (used for anchor IDs and sessionStorage keys).
 * @prop {boolean}                 active            - Whether this tab panel is currently visible.
 *                                                     Inactive tabs render an empty ARIA shell to avoid unnecessary work.
 * @prop {string|null}             code              - Submission source code (forwarded to children).
 * @prop {FeedbackEnvelopeContext} submissionContext - Submission context (forwarded to children).
 */
@customElement("d-feedback-tab")
export class FeedbackTab extends DodonaElement {
    @property({ type: Object }) tab!: Tab;
    @property({ type: Number, attribute: "tab-index" }) tabIdx: number = 0;
    @property({ type: Boolean }) active: boolean = false;
    @property({ type: String }) code: string | null = null;
    @property({ type: Object }) submissionContext: FeedbackEnvelopeContext = {};

    @state() private _diffStyle: DiffStyle = "split";
    @state() private _renderReady: boolean = false;
    // Collapse state per group, indexed like tab.groups.
    @state() private _collapsed: boolean[] = [];

    override willUpdate(changedProperties: Map<string, unknown>): void {
        if (changedProperties.has("tab") && this.tab) {
            this._diffStyle = tabDiffStyle(this.tab);
            this._initCollapsed();
            // New result data: paint the loading state first, the full render follows
            // (see updated()). This is the page load and the hand-in case.
            this._renderReady = false;
        } else if (changedProperties.has("active") && this.active && this.hasUpdated) {
            // A user switched to this tab: render it in the same frame as the switch.
            // A loading frame here would paint a short page and move the viewport.
            this._renderReady = true;
        }
    }

    private get _groups(): Context[] {
        return this.tab?.groups ?? [];
    }

    private _initCollapsed(): void {
        const expandAll = this._groups.every((g) => g.accepted);
        this._collapsed = this._groups.map((g, i) => this._savedCollapsed(i) ?? (g.accepted && !expandAll));
    }

    private _sessionKey(i: number): string | null {
        const id = this.submissionContext.submissionId;
        if (id === undefined) return null;
        return `dodona-collapse-${id}-tab-${this.tabIdx}-group-${i}`;
    }

    private _savedCollapsed(i: number): boolean | null {
        const key = this._sessionKey(i);
        if (key === null) return null;
        const saved = sessionStore.get(key);
        return saved === null ? null : saved === "true";
    }

    private _setCollapsed(i: number, collapsed: boolean): void {
        this._collapsed = this._collapsed.with(i, collapsed);
        const key = this._sessionKey(i);
        if (key !== null) sessionStore.set(key, String(collapsed));
    }

    get showCorrect(): boolean {
        return this._groups.some((g, i) => g.accepted && !this._collapsed[i]);
    }

    private _setShowCorrect(show: boolean): void {
        this._groups.forEach((g, i) => {
            if (g.accepted) this._setCollapsed(i, !show);
        });
    }

    override updated(changedProperties: Map<string, unknown>): void {
        super.updated(changedProperties);
        // Bootstrap tooltips on the switch buttons are not picked up by the
        // global initTooltips() that ran before this Lit subtree existed.
        // Only re-init when the switch buttons themselves enter the DOM: when
        // _renderReady flips, or when the tab is activated with it already set.
        if (
            window.bootstrap &&
            this.active &&
            this._renderReady &&
            (changedProperties.has("_renderReady") || changedProperties.has("active"))
        ) {
            initTooltips(this);
        }
        // After the loading state paints, trigger the full render.
        // Two rAFs are needed: the first lets the browser paint the loading
        // skeleton; the second ensures Lit's own microtask queue has flushed
        // before we flip _renderReady, so child components see a consistent state.
        if (this.active && !this._renderReady && (changedProperties.has("active") || changedProperties.has("tab"))) {
            requestAnimationFrame(() =>
                requestAnimationFrame(() => {
                    this._renderReady = true;
                }),
            );
        }
    }

    private _renderTabSummary(groups: Context[]): TemplateResult | "" {
        if (groups.length === 0) return "";
        const { correct, total } = countGroups(groups);
        const correctGroupLabel = I18n.t("js.submissions_show.correct_group");
        return html`
            <div class="tab-summary-text d-none d-md-block">
                ${correct}/${total} ${correctGroupLabel.toLowerCase()}:
            </div>
            <div class="tab-summary-icons d-none d-md-block">
                ${groups.map((g, i) => {
                    const statusLabel = I18n.t(
                        g.accepted ? "js.submissions_show.correct_group" : "js.submissions_show.wrong_group",
                    );
                    const ariaLabel = I18n.t("js.submissions_show.test_status", {
                        number: i + 1,
                        status: statusLabel,
                    });
                    return html`<div class="${g.accepted ? "correct" : "wrong"}">
                        <a
                            href="#tab-${this.tabIdx}-group-${i}"
                            title="#${i + 1}"
                            aria-label="${ariaLabel}"
                            @click=${() => this._setCollapsed(i, false)}
                        >
                            <i class="mdi mdi-12 ${g.accepted ? "mdi-check" : "mdi-close"}" aria-hidden="true"></i>
                        </a>
                    </div>`;
                })}
            </div>
        `;
    }

    private _renderCorrectSwitch(): TemplateResult {
        const correctTestsLabel = I18n.t("js.submissions_show.correct_tests");
        const correctShownLabel = I18n.t("js.submissions_show.correct.shown");
        const correctHiddenLabel = I18n.t("js.submissions_show.correct.hidden");
        return html`<span class="correct-switch-buttons switch-buttons">
            <span>${correctTestsLabel}</span>
            <div class="btn-group btn-toggle">
                <button
                    class="btn ${this.showCorrect ? "active" : ""}"
                    aria-pressed="${this.showCorrect}"
                    title="${correctShownLabel}"
                    aria-label="${correctShownLabel}"
                    data-bs-toggle="tooltip"
                    data-bs-placement="top"
                    @click=${() => this._setShowCorrect(true)}
                >
                    <i class="mdi mdi-eye" aria-hidden="true"></i>
                </button>
                <button
                    class="btn ${!this.showCorrect ? "active" : ""}"
                    aria-pressed="${!this.showCorrect}"
                    title="${correctHiddenLabel}"
                    aria-label="${correctHiddenLabel}"
                    data-bs-toggle="tooltip"
                    data-bs-placement="top"
                    @click=${() => this._setShowCorrect(false)}
                >
                    <i class="mdi mdi-eye-off" aria-hidden="true"></i>
                </button>
            </div>
        </span>`;
    }

    private _renderDiffSwitch(): TemplateResult {
        const outputLabel = I18n.t("js.submissions_show.output");
        const diffSplitLabel = I18n.t("js.submissions_show.diff.split");
        const diffUnifiedLabel = I18n.t("js.submissions_show.diff.unified");
        return html`<span class="diff-switch-buttons switch-buttons">
            <span>${outputLabel}</span>
            <div class="btn-group btn-toggle">
                <button
                    class="btn ${this._diffStyle === "split" ? "active" : ""}"
                    aria-pressed="${this._diffStyle === "split"}"
                    title="${diffSplitLabel}"
                    aria-label="${diffSplitLabel}"
                    data-bs-toggle="tooltip"
                    data-bs-placement="top"
                    @click=${() => (this._diffStyle = "split")}
                >
                    <i class="mdi mdi-arrow-split-vertical" aria-hidden="true"></i>
                </button>
                <button
                    class="btn ${this._diffStyle === "unified" ? "active" : ""}"
                    aria-pressed="${this._diffStyle === "unified"}"
                    title="${diffUnifiedLabel}"
                    aria-label="${diffUnifiedLabel}"
                    data-bs-toggle="tooltip"
                    data-bs-placement="top"
                    @click=${() => (this._diffStyle = "unified")}
                >
                    <i class="mdi mdi-arrow-split-horizontal" aria-hidden="true"></i>
                </button>
            </div>
        </span>`;
    }

    render(): TemplateResult {
        const tab = this.tab;
        if (!tab) return html``;

        const id = tabId(this.tabIdx);
        const triggerId = `${id}-trigger`;

        if (!this.active) {
            return html`<div
                class="tab-pane feedback-tab-pane"
                id="${id}"
                role="tabpanel"
                aria-labelledby="${triggerId}"
                tabindex="-1"
                aria-hidden="true"
            ></div>`;
        }

        if (!this._renderReady) {
            return html`<div
                class="tab-pane feedback-tab-pane active"
                id="${id}"
                role="tabpanel"
                aria-labelledby="${triggerId}"
                tabindex="0"
            >
                <div class="feedback-tab-loading">
                    <i class="mdi mdi-loading mdi-spin" aria-hidden="true"></i>
                    ${I18n.t("js.submissions_show.loading")}
                </div>
            </div>`;
        }

        const groups = this._groups;
        const showHideCorrectSwitch = groups.some((g) => g.accepted);
        const showDiffTypeSwitch = groups.some(
            (g) => g.groups?.some((tc) => tc.tests?.some((t) => !t.accepted)) ?? false,
        );

        return html`
            <div
                class="tab-pane feedback-tab-pane active"
                id="${id}"
                role="tabpanel"
                aria-labelledby="${triggerId}"
                tabindex="0"
            >
                <div class="feedback-table-options sticky">
                    <div class="tab-summary">${this._renderTabSummary(groups)}</div>
                    ${showHideCorrectSwitch ? this._renderCorrectSwitch() : ""}
                    ${showDiffTypeSwitch ? this._renderDiffSwitch() : ""}
                </div>

                ${tab.messages?.length ? renderMessages(tab.messages, this.submissionContext) : ""}

                <div class="groups">
                    ${groups.map(
                        (g, i) =>
                            html`<d-feedback-context
                                .context=${g}
                                .contextIndex=${i}
                                .tabIdx=${this.tabIdx}
                                .collapsed=${this._collapsed[i]}
                                .diffStyle=${this._diffStyle}
                                .code=${this.code}
                                .submissionContext=${this.submissionContext}
                                @toggle-collapse=${() => this._setCollapsed(i, !this._collapsed[i])}
                            ></d-feedback-context>`,
                    )}
                </div>
            </div>
        `;
    }
}
